import {
  AlertTriangle,
  Clock,
  DollarSign,
  Eye,
  Lightbulb,
  RotateCcw,
  Settings as SettingsIcon,
  UserPlus,
  Video,
  Wallet,
  type LucideIcon,
} from 'lucide-react'

export type FeedItemType =
  | 'offer'
  | 'setup'
  | 'manager_invite'
  | 'revision'
  | 'overdue'
  | 'due_soon'
  | 'creative_suggestion'
  | 'video_suggestion'
  | 'upcoming'
  | 'pending_approval'
  | 'payment_received'

export interface FeedItem {
  id: string
  type: FeedItemType
  /** Lower number = higher urgency. Used as the primary sort key. */
  priority: number
  title: string
  subtitle: string
  ctaLabel: string
  ctaPath: string
  brandName: string
  /** Tiebreaker within a priority bucket — ISO date string. Older = more urgent. */
  sortDate?: string
}

export interface FeedTypeStyle {
  label: string
  icon: LucideIcon
  /** Hex color used for icon + label tag. */
  color: string
  /** Tailwind class for the card background tint. */
  bg: string
  /** Tailwind class for the card border tint. */
  border: string
  /**
   * Sort key for the feed. Lower = higher urgency.
   * The full ordering lives here so the priority table is readable at a glance —
   * change a number here and the feed re-sorts.
   */
  priority: number
}

export const typeConfig: Record<FeedItemType, FeedTypeStyle> = {
  offer: {
    priority: 1,
    label: 'NEW OFFER',
    icon: DollarSign,
    color: '#C0FD72',
    bg: 'bg-[#C0FD72]/10',
    border: 'border-[#C0FD72]/25',
  },
  setup: {
    priority: 2,
    label: 'FINISH SETUP',
    icon: SettingsIcon,
    color: '#9CF0FB',
    bg: 'bg-cyan-400/10',
    border: 'border-cyan-400/25',
  },
  manager_invite: {
    priority: 3,
    label: 'MANAGER INVITE',
    icon: UserPlus,
    color: '#C084FC',
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/25',
  },
  overdue: {
    priority: 3,
    label: 'OVERDUE',
    icon: AlertTriangle,
    color: '#ef4444',
    bg: 'bg-red-500/10',
    border: 'border-red-500/25',
  },
  revision: {
    priority: 3,
    label: 'REVISION REQUESTED',
    icon: RotateCcw,
    color: '#f59e0b',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/25',
  },
  due_soon: {
    priority: 4,
    label: 'DUE SOON',
    icon: Clock,
    color: '#a855f7',
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/25',
  },
  creative_suggestion: {
    priority: 4,
    label: 'MANAGER SUGGESTION',
    icon: Lightbulb,
    color: '#FACC15',
    bg: 'bg-yellow-400/10',
    border: 'border-yellow-400/25',
  },
  video_suggestion: {
    priority: 4,
    label: 'MANAGER SHARED A VIDEO',
    icon: Video,
    color: '#FACC15',
    bg: 'bg-yellow-400/10',
    border: 'border-yellow-400/25',
  },
  upcoming: {
    priority: 5,
    label: 'UPCOMING',
    icon: Clock,
    color: '#60a5fa',
    bg: 'bg-blue-400/10',
    border: 'border-blue-400/20',
  },
  pending_approval: {
    priority: 6,
    label: 'PENDING BRAND APPROVAL',
    icon: Eye,
    color: '#a855f7',
    bg: 'bg-purple-500/8',
    border: 'border-purple-500/20',
  },
  payment_received: {
    priority: 7,
    label: 'PAYMENT',
    icon: Wallet,
    color: '#C0FD72',
    bg: 'bg-[#C0FD72]/8',
    border: 'border-[#C0FD72]/20',
  },
}
